import { users } from '@/mocks/users';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import { replies } from '@/mocks/replies';
import { weeklyActiveUsers } from '@/mocks/activity';

export interface User {
  id: string;
  name: string;
  avatar: string;
  bio: string;
  joinDate: string;
  following: number;
  followers: number;
  postCount: number;
  replyCount: number;
  badges: string[];
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  image: string;
  topicCount: number;
}

export interface Topic {
  id: string;
  title: string;
  categoryId: string;
  authorId: string;
  cover: string;
  summary: string;
  tags: string[];
  replyCount: number;
  viewCount: number;
  likeCount: number;
  createdAt: string;
  isHot: boolean;
  content: string;
}

export interface Reply {
  id: string;
  topicId: string;
  userId: string;
  floor: number;
  likes: number;
  createdAt: string;
  content: string;
}

export interface TopicWithMeta extends Topic {
  author: User;
  category: Category;
}

const allUsers = users as User[];
const allCategories = categories as Category[];
const allTopics = topics as Topic[];
const allReplies = replies as Reply[];

export const getUserById = (id: string): User | undefined => allUsers.find((u) => u.id === id);

export const getCategoryById = (id: string): Category | undefined =>
  allCategories.find((c) => c.id === id);

export const getAllCategories = (): Category[] => allCategories;

export const getAllUsers = (): User[] => allUsers;

export const getTopicById = (id: string): Topic | undefined => allTopics.find((t) => t.id === id);

export const getAllTopics = (): Topic[] => allTopics;

export function withMeta(topic: Topic): TopicWithMeta {
  return {
    ...topic,
    author: getUserById(topic.authorId) as User,
    category: getCategoryById(topic.categoryId) as Category,
  };
}

export const getHotTopics = (limit = 5): TopicWithMeta[] =>
  allTopics.filter((t) => t.isHot).slice(0, limit).map(withMeta);

export const getLatestTopics = (): TopicWithMeta[] =>
  [...allTopics].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)).map(withMeta);

export const getTopicsByCategory = (categoryId: string): TopicWithMeta[] =>
  allTopics.filter((t) => t.categoryId === categoryId).map(withMeta);

export const getRelatedTopics = (topic: Topic, limit = 5): TopicWithMeta[] =>
  allTopics
    .filter((t) => t.categoryId === topic.categoryId && t.id !== topic.id)
    .slice(0, limit)
    .map(withMeta);

export const getRepliesByTopic = (topicId: string): Reply[] =>
  allReplies.filter((r) => r.topicId === topicId).sort((a, b) => a.floor - b.floor);

export const getUserTopics = (userId: string): TopicWithMeta[] =>
  allTopics
    .filter((t) => t.authorId === userId)
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
    .map(withMeta);

export const getUserReplies = (userId: string): Reply[] =>
  allReplies
    .filter((r) => r.userId === userId)
    .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));

export const getWeeklyActive = (): Array<{ user: User; weeklyPosts: number; weeklyReplies: number }> =>
  weeklyActiveUsers
    .map((w) => ({ user: getUserById(w.userId) as User, weeklyPosts: w.weeklyPosts, weeklyReplies: w.weeklyReplies }))
    .filter((w) => Boolean(w.user));

export type TopicSort = 'latest' | 'hot' | 'replies';

export function sortTopics(list: TopicWithMeta[], sort: TopicSort): TopicWithMeta[] {
  const copy = [...list];
  if (sort === 'hot') return copy.sort((a, b) => b.viewCount + b.likeCount * 3 - (a.viewCount + a.likeCount * 3));
  if (sort === 'replies') return copy.sort((a, b) => b.replyCount - a.replyCount);
  return copy.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
}

export type SearchSort = 'relevance' | 'time';

export function searchTopics(keyword: string): TopicWithMeta[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allTopics
    .filter((t) => {
      const author = getUserById(t.authorId);
      const category = getCategoryById(t.categoryId);
      return (
        t.title.toLowerCase().includes(kw) ||
        t.summary.toLowerCase().includes(kw) ||
        t.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        (author?.name.toLowerCase().includes(kw) ?? false) ||
        (category?.name.toLowerCase().includes(kw) ?? false)
      );
    })
    .map(withMeta);
}

export function sortSearchResults(list: TopicWithMeta[], keyword: string, sort: SearchSort): TopicWithMeta[] {
  if (sort === 'time') return [...list].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
  const kw = keyword.trim().toLowerCase();
  const score = (t: TopicWithMeta) => {
    let s = 0;
    if (t.title.toLowerCase().includes(kw)) s += 10;
    if (t.tags.some((tag) => tag.toLowerCase().includes(kw))) s += 6;
    if (t.summary.toLowerCase().includes(kw)) s += 3;
    return s;
  };
  return [...list].sort((a, b) => score(b) - score(a) || b.replyCount - a.replyCount);
}

export function relatedKeywords(keyword: string, limit = 6): string[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const pool = new Set<string>();
  allTopics.forEach((t) => {
    if (t.title.toLowerCase().includes(kw)) t.tags.forEach((tag) => pool.add(tag));
  });
  return Array.from(pool).slice(0, limit);
}